import { generateKeyBetween } from 'fractional-indexing';

/**
 * Compare two fractional-indexing positions by raw code-unit order.
 *
 * fractional-indexing keys are only guaranteed to sort correctly under plain
 * lexicographic (byte/code-unit) comparison. `localeCompare` applies
 * locale-aware collation that disagrees on case, digit-vs-letter boundaries,
 * and punctuation — which silently scrambles drag-and-drop ordering.
 */
export function comparePositions(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
import { TaskWithAssignments } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

/**
 * Which container's ordering field to read from a task: sprints use
 * `sprintOrder`, buckets use `bucketOrder`, everything else uses the
 * fractional `order` string.
 */
export type TaskPositionContext = 'sprint' | 'backlog' | 'group' | 'bucket';

const logger = createLogger('utils/fractional-ordering');

/**
 * Fractional Indexing Utilities
 *
 * This module provides industry-standard fractional indexing for task ordering.
 * Used by companies like Figma, Notion, Linear for perfect ordering without collisions.
 */

// Debug logging is disabled in production
const DEBUG_FRACTIONAL_ORDERING = import.meta.env.DEV && localStorage.getItem('DEBUG_FRACTIONAL_ORDERING') === 'true';
function debugLog(...args: unknown[]): void {
  if (DEBUG_FRACTIONAL_ORDERING) {
  }
}

function debugWarn(...args: unknown[]): void {
  if (DEBUG_FRACTIONAL_ORDERING) {
    logger.warn(...args);
  }
}

function debugError(...args: unknown[]): void {
  if (DEBUG_FRACTIONAL_ORDERING) {
    logger.error(...args);
  }
}

export interface PositionUpdate {
  order?: string | null;
}

/**
 * Validation result for fractional indices
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
  normalized?: string;
}

/**
 * Comprehensive validation for fractional indices
 */
export function validateFractionalIndex(position: string | null | undefined, taskId?: string): ValidationResult {
  if (position === null || position === undefined) {
    return {
      isValid: false,
      error: 'Position cannot be null or undefined',
      normalized: generateKeyBetween(null, null) || 'a1'
    };
  }

  if (typeof position !== 'string') {
    return {
      isValid: false,
      error: 'Position must be a string',
      normalized: generateKeyBetween(null, null) || 'a1'
    };
  }

  if (position.length === 0) {
    return {
      isValid: false,
      error: 'Position cannot be empty',
      normalized: generateKeyBetween(null, null) || 'a1'
    };
  }

  // Check for obviously invalid patterns
  if (position.includes(' ') || position.includes('\t') || position.includes('\n')) {
    return {
      isValid: false,
      error: 'Position cannot contain whitespace',
      normalized: validateAndNormalizePosition(position, taskId)
    };
  }

  // Check if it's a valid fractional index by testing operations
  try {
    // Test if we can generate keys around it
    generateKeyBetween(position, null);
    generateKeyBetween(null, position);
    
    return {
      isValid: true,
      normalized: position
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Invalid fractional index: ${error instanceof Error ? error.message : 'Unknown error'}`,
      normalized: validateAndNormalizePosition(position, taskId)
    };
  }
}

/**
 * Validate and normalize a fractional index, ensuring it's safe to use
 */
export function normalizeFractionalIndex(position: string | null | undefined, taskId?: string): string {
  const result = validateFractionalIndex(position, taskId);
  return result.normalized || generateKeyBetween(null, null) || 'a1';
}

/**
 * Check if a fractional index is within acceptable bounds (not too long/complex)
 */
export function isFractionalIndexOptimal(position: string): boolean {
  if (!position || typeof position !== 'string') {
    return false;
  }

  // Check length - overly long indices indicate need for rebalancing
  if (position.length > 50) {
    return false;
  }

  // Check for deeply nested patterns that might cause performance issues
  const complexityScore = position.split('').reduce((score, char) => {
    // Characters closer to 'a' or 'z' in the alphabet indicate more nesting
    const charCode = char.charCodeAt(0);
    if (charCode >= 97 && charCode <= 122) { // lowercase a-z
      const distance = Math.min(charCode - 97, 122 - charCode); // Distance from edges
      return score + (10 - distance); // Higher score for characters closer to edges
    }
    return score;
  }, 0);

  // If complexity score is too high, suggest rebalancing
  return complexityScore < position.length * 5;
}

/**
 * Validate an array of fractional indices for consistency
 */
export function validateFractionalIndexArray(positions: string[]): { 
  isValid: boolean; 
  errors: string[];
  duplicates: string[];
  sortingIssues: string[];
} {
  const errors: string[] = [];
  const duplicates: string[] = [];
  const sortingIssues: string[] = [];

  // Check individual validity
  positions.forEach((pos, index) => {
    const validation = validateFractionalIndex(pos);
    if (!validation.isValid) {
      errors.push(`Position ${index}: ${validation.error}`);
    }
  });

  // Check for duplicates
  const positionCounts = new Map<string, number>();
  positions.forEach(pos => {
    const count = positionCounts.get(pos) || 0;
    positionCounts.set(pos, count + 1);
    if (count === 1) {
      duplicates.push(pos);
    }
  });

  // Check sorting order
  const sorted = [...positions].sort((a, b) => comparePositions(a, b));
  positions.forEach((pos, index) => {
    if (pos !== sorted[index]) {
      sortingIssues.push(`Position at index ${index} (${pos}) is out of order`);
    }
  });

  return {
    isValid: errors.length === 0 && duplicates.length === 0 && sortingIssues.length === 0,
    errors,
    duplicates,
    sortingIssues
  };
}

/**
 * Get the appropriate position field value for a task based on context
 */
export function getTaskPosition(task: TaskWithAssignments, context: TaskPositionContext = 'group'): string | null {
  const positionValue =
    context === 'sprint' && task.sprintOrder != null
      ? task.sprintOrder
      : context === 'bucket' && task.bucketOrder != null
        ? task.bucketOrder
        : task.order;
  
  // Handle string positions (fractional indexing)
  if (typeof positionValue === 'string') {
    return validateAndNormalizePosition(positionValue, task.id);
  }
  
  // Handle numeric positions (legacy)
  return positionValue ? convertIntegerToPosition(positionValue) : convertIntegerToPosition(1);
}

/**
 * Validate and normalize position strings, converting invalid ones to valid fractional positions
 */
function validateAndNormalizePosition(position: string, taskId?: string): string {
  // Handle our legacy "n" default and simple alphabet positions
  if (position === 'n' || /^n[a-z]*$/.test(position)) {
    // Convert simple alphabet positions to valid fractional keys
    const alphabetPart = position.substring(1); // Remove 'n' prefix
    if (alphabetPart.length === 0) {
      // Just 'n' - use middle position but make it unique using task ID
      if (taskId) {
        const uniqueHash = hashString(position + taskId);
        return generateNthFractionalPosition(uniqueHash);
      }
      const defaultPosition = generateKeyBetween(null, null) || 'a1';
      // Ensure we never return a0 - convert to a1 to match our migration script
      return defaultPosition === 'a0' ? 'a1' : defaultPosition;
    }
    // Convert alphabet sequence to integer for consistent positioning
    let value = 0;
    for (let i = 0; i < alphabetPart.length; i++) {
      value = value * 26 + (alphabetPart.charCodeAt(i) - 96); // a=1, b=2, etc.
    }
    // Add task ID hash to make positions unique even if they have the same invalid position
    if (taskId) {
      value += hashString(taskId) % 100; // Add a small unique offset
    }
    return generateNthFractionalPosition(value);
  }
  
  // Handle other invalid patterns like 'Zz', single letters, etc.
  if (position && position.length > 0) {
    try {
      // Test if it's a valid position by trying to generate a key after it
      generateKeyBetween(position, null);
      return position;
    } catch {
      // If invalid, convert to a safe position based on hash
      // Use hash to ensure different invalid positions get different valid positions
      let hashValue = hashString(position);
      if (taskId) {
        hashValue += hashString(taskId) % 100; // Add task ID for uniqueness
      }
      return generateNthFractionalPosition(hashValue);
    }
  }
  
  const fallbackPosition = generateKeyBetween(null, null) || 'a1';
  // Ensure we never return a0 - convert to a1 to match our migration script
  return fallbackPosition === 'a0' ? 'a1' : fallbackPosition;
}

/**
 * Simple string hash function for converting invalid positions
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % 1000 + 1; // Ensure positive, reasonable range
}

/**
 * Convert legacy integer order to fractional position for smooth migration
 */
export function convertIntegerToPosition(integerOrder: number | string | null | undefined): string {
  if (integerOrder === null || integerOrder === undefined) {
    const position = generateKeyBetween(null, null) || 'a1';
    // Ensure we never return a0 - convert to a1 to match our migration script
    return position === 'a0' ? 'a1' : position;
  }
  
  // If already a string (fractional position), validate and return
  if (typeof integerOrder === 'string') {
    return validateAndNormalizePosition(integerOrder);
  }
  
  // Convert integer to valid fractional position
  // Generate a sequence of positions based on the integer value
  const normalizedInt = Math.max(0, Math.floor(Number(integerOrder)));
  return generateNthFractionalPosition(normalizedInt);
}

/**
 * Generate nth fractional position using the library's generateKeyBetween
 */
function generateNthFractionalPosition(n: number): string {
  if (n === 0) {
    const initialPosition = generateKeyBetween(null, null) || 'a1';
    // Ensure we never return a0 - convert to a1 to match our migration script
    return initialPosition === 'a0' ? 'a1' : initialPosition;
  }
  
  // Generate a sequence of valid fractional positions
  let current = generateKeyBetween(null, null) || 'a1';
  // Ensure we never start with a0
  if (current === 'a0') {
    current = 'a1';
  }
  
  for (let i = 1; i < n && i < 100; i++) { // Limit to prevent infinite loops
    const next = generateKeyBetween(current, null);
    if (next) {
      current = next;
    } else {
      // If we can't generate more, create a simple increment
      break;
    }
  }
  
  return current;
}

/**
 * Simplified position generation for @dnd-kit integration
 */
export function generateInsertPosition(
  before: string | null,
  after: string | null
): string {
  if (before !== null && after !== null) {
    // Guard against reversed bounds. If reversed, swap so we always pass (lower, higher)
    if (before > after) {
      const position = generateKeyBetween(after, before);
      // Ensure we never return a0 - convert to a1 to match our migration script
      if (position === 'a0') {
        return 'a1';
      }
      return position;
    }

    // If equal, we're in a duplicate cluster. Default to placing after the cluster.
    // Callers who want "above" should provide bounds accordingly; this keeps
    // the helper deterministic and collision-free.
    if (before === after) {
      const position = generateKeyBetween(before, null);
      if (position === 'a0') {
        return 'a1';
      }
      return position;
    }
  }

  // Standard fractional-indexing, generate a key between two neighbors.
  const position = generateKeyBetween(before, after);
  
  // Ensure we never return a0 - convert to a1 to match our migration script
  if (position === 'a0') {
    return 'a1';
  }
  
  return position;
}

/**
 * Validate and ensure a position is unique among existing positions
 */
function validateAndEnsureUniquePosition(
  proposedPosition: string,
  existingPositions: string[],
  context: string
): string {
  // Check if the proposed position is already used
  if (existingPositions.includes(proposedPosition)) {
    debugWarn(`[FractionalOrdering] Duplicate position detected: ${proposedPosition} in context: ${context}`);
    
    // For first position conflicts, try some guaranteed first positions
    if (context === 'first-position') {
      const firstPositionCandidates = ['_', '`', '^', ']', '\\', '[', '@', '?', '>', '=', '<', ';', ':'];
      for (const candidate of firstPositionCandidates) {
        if (!existingPositions.includes(candidate)) {
          debugLog(`[FractionalOrdering] Using first position candidate: ${candidate}`);
          return candidate;
        }
      }
      
      // If all candidates are taken, use a prefix approach
      let candidate = '_' + Date.now().toString(36);
      debugLog(`[FractionalOrdering] Using timestamp-based first position: ${candidate}`);
      return candidate;
    }
    
    // For other contexts, use simple strategies
    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).substring(2, 5);
    
    // Try different strategies to create a unique position
    const strategies = [
      () => proposedPosition + 'x' + randomSuffix,
      () => proposedPosition + timestamp,
      () => `${proposedPosition}${randomSuffix}`,
      () => `z${timestamp}${randomSuffix}` // Fallback to end
    ];
    
    for (const strategy of strategies) {
      const candidate = strategy();
      if (!existingPositions.includes(candidate)) {
        debugLog(`[FractionalOrdering] Generated unique position: ${candidate} (was: ${proposedPosition})`);
        return candidate;
      }
    }
    
    // Ultimate fallback - should never happen
    return `z${Date.now()}${Math.random().toString(36).substring(2)}`;
  }
  
  return proposedPosition;
}

/**
 * Generate position for a new task (at the end of the list)
 */
export function generateNewTaskPosition(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group'
): string {
  const sortedTasks = sortTasksByPosition(tasks, context);
  const lastTask = sortedTasks.length > 0 ? sortedTasks[sortedTasks.length - 1] : null;
  const lastPosition = lastTask ? getTaskPosition(lastTask, context) : null;
  return generateInsertPosition(lastPosition, null);
}

/**
 * Get all tasks in the same position context (for reordering)
 */
export function getContextTasksWithPositions(
  allTasks: TaskWithAssignments[],
  targetTask: TaskWithAssignments,
  isSprintContext: boolean = false
): TaskWithAssignments[] {
  // This should be used with your existing getContextTasks logic
  // but ensures we're working with the right position field
  return allTasks.filter(task => {
    if (isSprintContext) {
      return task.sprintId === targetTask.sprintId;
    } else {
      return (!task.sprintId && task.groupId === targetTask.groupId) ||
             (!task.sprintId && !task.groupId && !targetTask.groupId);
    }
  });
}

/**
 * Sort tasks by their position (fractional or legacy integer)
 */
export function sortTasksByPosition(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group'
): TaskWithAssignments[] {
  return [...tasks].sort((a, b) => {
    const posA = getTaskPosition(a, context) || 'z';
    const posB = getTaskPosition(b, context) || 'z';

    const positionComparison = comparePositions(posA, posB);
    if (positionComparison !== 0) {
      return positionComparison;
    }

    // Tie-breaker using creation date for stability, which is crucial for drag-and-drop.
    const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aCreated - bCreated;
  });
}

/**
 * Analyze and detect duplicate positions in a task list
 */
export function analyzeTaskPositions(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group',
  label: string = 'Tasks'
): {
  totalTasks: number;
  uniquePositions: number;
  duplicatePositions: string[];
  duplicateCount: number;
  analysis: string;
} {
  const positions = tasks.map(task => getTaskPosition(task, context) || 'unknown');
  const positionCounts = new Map<string, number>();
  
  // Count occurrences of each position
  positions.forEach(pos => {
    positionCounts.set(pos, (positionCounts.get(pos) || 0) + 1);
  });
  
  // Find duplicates
  const duplicatePositions = Array.from(positionCounts.entries())
    .filter(([_, count]) => count > 1)
    .map(([pos, _]) => pos);
  
  const duplicateCount = duplicatePositions.reduce((sum, pos) => sum + positionCounts.get(pos)! - 1, 0);
  
  let analysis = `${label}: ${tasks.length} tasks, ${positionCounts.size} unique positions`;
  if (duplicateCount > 0) {
    analysis += `, ${duplicateCount} duplicate positions detected`;
  }
  
  debugLog(`[FractionalOrdering] ${analysis}`);
  
  if (duplicatePositions.length > 0) {
    debugWarn(`[FractionalOrdering] Duplicate positions in ${label}:`, 
      duplicatePositions.map(pos => ({
        position: pos,
        count: positionCounts.get(pos),
        tasks: tasks.filter(t => getTaskPosition(t, context) === pos).map(t => ({
          id: t.id.slice(-6),
          name: t.name.slice(0, 20)
        }))
      }))
    );
  }
  
  return {
    totalTasks: tasks.length,
    uniquePositions: positionCounts.size,
    duplicatePositions,
    duplicateCount,
    analysis
  };
}

/**
 * Enhanced debug helper with duplicate detection
 */
export function debugTaskPositions(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group',
  label: string = 'Tasks'
): void {
  const analysis = analyzeTaskPositions(tasks, context, label);
  
  debugLog(`[DEBUG] ${label} positions:`, 
    tasks.map(task => ({
      id: task.id.slice(-6),
      name: task.name.slice(0, 20),
      position: getTaskPosition(task, context),
      order: task.order
    }))
  );
  
  if (analysis.duplicateCount > 0) {
    debugError(`[DEBUG] ${label} has ${analysis.duplicateCount} duplicate positions that need fixing!`);
  }
}

/**
 * Check if a list of tasks needs position compaction
 */
export function needsPositionCompaction(tasks: TaskWithAssignments[]): boolean {
  const positions = tasks
    .map(task => getTaskPosition(task))
    .filter(pos => pos !== null) as string[];

  // Check if any position is too long
  const hasLongPositions = positions.some(pos => pos.length > 30);
  
  // Check average position length
  const avgLength = positions.reduce((sum, pos) => sum + pos.length, 0) / positions.length;
  
  // Check for positions that are not optimal
  const suboptimalCount = positions.filter(pos => !isFractionalIndexOptimal(pos)).length;
  const suboptimalRatio = suboptimalCount / positions.length;

  return hasLongPositions || avgLength > 15 || suboptimalRatio > 0.3;
}

/**
 * Compact fractional indices for a list of tasks
 */
export function compactTaskPositions(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group'
): Array<{ taskId: string; oldOrder: string; newOrder: string }> {
  // Sort tasks by their current positions
  const sortedTasks = sortTasksByPosition(tasks, context);
  
  // Generate new evenly distributed positions
  const compactionResults: Array<{ taskId: string; oldOrder: string; newOrder: string }> = [];
  
  // Generate new positions with even spacing
  let currentPosition = generateKeyBetween(null, null) || 'a1';
  
  sortedTasks.forEach((task, index) => {
    const oldOrder = getTaskPosition(task, context) || 'a1';
    let newOrder: string;
    
    if (index === 0) {
      // First task gets the initial position
      newOrder = currentPosition;
    } else {
      // Generate position after the current position
      const nextPos = generateKeyBetween(currentPosition, null);
      newOrder = nextPos || `${currentPosition}a`;
      currentPosition = newOrder;
    }
    
    // Only include if the position actually changes
    if (oldOrder !== newOrder) {
      compactionResults.push({
        taskId: task.id,
        oldOrder,
        newOrder
      });
    }
  });
  
  debugLog(`[FractionalOrdering] Compaction would update ${compactionResults.length} out of ${tasks.length} task positions`);
  
  return compactionResults;
}

/**
 * Auto-compaction utility that suggests when compaction is needed
 */
export function analyzePositionHealth(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group'
): {
  needsCompaction: boolean;
  stats: {
    totalTasks: number;
    averageLength: number;
    maxLength: number;
    suboptimalCount: number;
    duplicateCount: number;
  };
  recommendation: string;
} {
  const positions = tasks
    .map(task => getTaskPosition(task, context))
    .filter(pos => pos !== null) as string[];

  const stats = {
    totalTasks: tasks.length,
    averageLength: positions.length > 0 ? positions.reduce((sum, pos) => sum + pos.length, 0) / positions.length : 0,
    maxLength: positions.length > 0 ? Math.max(...positions.map(pos => pos.length)) : 0,
    suboptimalCount: positions.filter(pos => !isFractionalIndexOptimal(pos)).length,
    duplicateCount: positions.length - new Set(positions).size
  };

  const needsCompaction = needsPositionCompaction(tasks);
  
  let recommendation = 'Positions are optimal';
  if (stats.duplicateCount > 0) {
    recommendation = 'Critical: Duplicate positions detected, immediate compaction required';
  } else if (stats.maxLength > 50) {
    recommendation = 'High: Very long positions detected, compaction recommended';
  } else if (stats.averageLength > 20) {
    recommendation = 'Medium: Average position length is high, consider compaction';
  } else if (stats.suboptimalCount / stats.totalTasks > 0.5) {
    recommendation = 'Low: Many suboptimal positions, compaction may improve performance';
  }

  return {
    needsCompaction,
    stats,
    recommendation
  };
}

/**
 * Perform automatic compaction if criteria are met
 */
export function autoCompactIfNeeded(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group',
  options: {
    force?: boolean;
    maxLength?: number;
    maxAverageLength?: number;
  } = {}
): Array<{ taskId: string; oldOrder: string; newOrder: string }> | null {
  const { force = false, maxLength = 40, maxAverageLength = 20 } = options;
  
  if (force) {
    return compactTaskPositions(tasks, context);
  }
  
  const analysis = analyzePositionHealth(tasks, context);
  
  // Auto-compact if positions are getting unwieldy
  if (
    analysis.stats.maxLength > maxLength ||
    analysis.stats.averageLength > maxAverageLength ||
    analysis.stats.duplicateCount > 0
  ) {
    debugLog(`[FractionalOrdering] Auto-compacting positions: ${analysis.recommendation}`);
    return compactTaskPositions(tasks, context);
  }
  
  return null;
} 

/**
 * Automatically recover from duplicate positions by reassigning unique positions
 */
export function recoverDuplicatePositions(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group'
): Array<{ taskId: string; oldOrder: string; newOrder: string; reason: string }> {
  const analysis = analyzeTaskPositions(tasks, context, 'Recovery analysis');
  
  if (analysis.duplicateCount === 0) {
    debugLog('[FractionalOrdering] No duplicate positions to recover');
    return [];
  }
  
  debugLog(`[FractionalOrdering] Starting recovery for ${analysis.duplicateCount} duplicate positions`);
  
  const recoveryResults: Array<{ taskId: string; oldOrder: string; newOrder: string; reason: string }> = [];
  const positionCounts = new Map<string, TaskWithAssignments[]>();
  
  // Group tasks by position
  tasks.forEach(task => {
    const position = getTaskPosition(task, context) || 'unknown';
    if (!positionCounts.has(position)) {
      positionCounts.set(position, []);
    }
    positionCounts.get(position)!.push(task);
  });
  
  // Find and fix duplicates
  positionCounts.forEach((tasksWithSamePosition, position) => {
    if (tasksWithSamePosition.length > 1) {
      debugLog(`[FractionalOrdering] Recovering ${tasksWithSamePosition.length} tasks with duplicate position: ${position}`);
      
      // Keep the first task (by creation date) in the original position
      const sortedTasks = tasksWithSamePosition.sort((a, b) => {
        const aCreated = new Date(a.createdAt).getTime();
        const bCreated = new Date(b.createdAt).getTime();
        return aCreated - bCreated;
      });
      
      // Generate new unique positions for the rest
      for (let i = 1; i < sortedTasks.length; i++) {
        const task = sortedTasks[i];
        const timestamp = Date.now().toString(36);
        const randomSuffix = Math.random().toString(36).substring(2, 5);
        const newPosition = `${position}_${timestamp}_${randomSuffix}`;
        
        recoveryResults.push({
          taskId: task.id,
          oldOrder: position,
          newOrder: newPosition,
          reason: `Duplicate position recovery - kept oldest task (${sortedTasks[0].id.slice(-6)}) in original position`
        });
      }
    }
  });
  
  debugLog(`[FractionalOrdering] Recovery complete: ${recoveryResults.length} positions reassigned`);
  
  return recoveryResults;
}

/**
 * Validate task list and recover from duplicates if needed
 */
export function validateAndRecoverTaskPositions(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group',
  options: {
    autoRecover?: boolean;
    logLevel?: 'debug' | 'warn' | 'error';
  } = {}
): {
  isValid: boolean;
  analysis: ReturnType<typeof analyzeTaskPositions>;
  recoveryActions?: Array<{ taskId: string; oldOrder: string; newOrder: string; reason: string }>;
} {
  const { autoRecover = false, logLevel = 'warn' } = options;
  
  const analysis = analyzeTaskPositions(tasks, context, 'Validation');
  const isValid = analysis.duplicateCount === 0;
  
  if (!isValid) {
    const message = `Task position validation failed: ${analysis.duplicateCount} duplicate positions detected`;
    
    if (logLevel === 'error') {
      debugError(`[FractionalOrdering] ${message}`);
    } else {
      debugWarn(`[FractionalOrdering] ${message}`);
    }
    
    if (autoRecover) {
      const recoveryActions = recoverDuplicatePositions(tasks, context);
      return {
        isValid: false,
        analysis,
        recoveryActions
      };
    }
  }
  
  return {
    isValid,
    analysis
  };
} 

/**
 * Position Recovery Service
 * Provides utilities for fixing duplicate positions in the database
 */
export class PositionRecoveryService {
  private static instance: PositionRecoveryService;
  
  static getInstance(): PositionRecoveryService {
    if (!PositionRecoveryService.instance) {
      PositionRecoveryService.instance = new PositionRecoveryService();
    }
    return PositionRecoveryService.instance;
  }
  
  /**
   * Execute position recovery by updating tasks with duplicate positions
   */
  async executeRecovery(
    recoveryActions: Array<{ taskId: string; oldOrder: string; newOrder: string; reason: string }>,
    updateTaskFunction: (taskId: string, updates: { order: string }) => Promise<any>
  ): Promise<{
    success: boolean;
    updatedTasks: number;
    errors: Array<{ taskId: string; error: string }>;
  }> {
    debugLog(`[PositionRecovery] Starting recovery for ${recoveryActions.length} tasks`);
    
    const errors: Array<{ taskId: string; error: string }> = [];
    let updatedTasks = 0;
    
    for (const action of recoveryActions) {
      try {
        debugLog(`[PositionRecovery] Updating task ${action.taskId}: ${action.oldOrder} -> ${action.newOrder}`);
        await updateTaskFunction(action.taskId, { order: action.newOrder });
        updatedTasks++;
        
        // Small delay to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        debugError(`[PositionRecovery] Failed to update task ${action.taskId}:`, errorMessage);
        errors.push({ taskId: action.taskId, error: errorMessage });
      }
    }
    
    const success = errors.length === 0;
    debugLog(`[PositionRecovery] Recovery complete: ${updatedTasks} updated, ${errors.length} errors`);
    
    return {
      success,
      updatedTasks,
      errors
    };
  }
  
  /**
   * Smart recovery that analyzes and fixes duplicate positions
   */
  async smartRecovery(
    tasks: TaskWithAssignments[],
    context: TaskPositionContext = 'group',
    updateTaskFunction: (taskId: string, updates: { order: string }) => Promise<any>
  ): Promise<{
    success: boolean;
    analysis: ReturnType<typeof analyzeTaskPositions>;
    recoveryResults?: {
      success: boolean;
      updatedTasks: number;
      errors: Array<{ taskId: string; error: string }>;
    };
  }> {
    const validation = validateAndRecoverTaskPositions(tasks, context, {
      autoRecover: true,
      logLevel: 'warn'
    });
    
    if (validation.isValid) {
      debugLog('[PositionRecovery] No recovery needed - all positions are unique');
      return {
        success: true,
        analysis: validation.analysis
      };
    }
    
    if (!validation.recoveryActions || validation.recoveryActions.length === 0) {
      debugWarn('[PositionRecovery] Validation failed but no recovery actions were generated');
      return {
        success: false,
        analysis: validation.analysis
      };
    }
    
    const recoveryResults = await this.executeRecovery(
      validation.recoveryActions,
      updateTaskFunction
    );
    
    return {
      success: recoveryResults.success,
      analysis: validation.analysis,
      recoveryResults
    };
  }
}

/**
 * Convenience function for quick position recovery
 */
export async function recoverTaskPositions(
  tasks: TaskWithAssignments[],
  context: TaskPositionContext = 'group',
  updateTaskFunction: (taskId: string, updates: { order: string }) => Promise<any>
): Promise<boolean> {
  const recoveryService = PositionRecoveryService.getInstance();
  const result = await recoveryService.smartRecovery(tasks, context, updateTaskFunction);
  
  if (result.success) {
    debugLog('[PositionRecovery] Position recovery completed successfully');
    return true;
  } else {
    debugError('[PositionRecovery] Position recovery failed:', result.recoveryResults?.errors);
    return false;
  }
} 

/**
 * Generic entity with order field for fractional ordering
 */
export interface OrderedEntity {
  id: string;
  order: string;
  createdAt: Date | string;
}

/**
 * TaskGroup interface for ordering
 */
export interface TaskGroupForOrdering extends OrderedEntity {
  name: string;
  moduleType?: string;
  moduleId?: string;
}

/**
 * Sprint interface for ordering
 */
export interface SprintForOrdering extends OrderedEntity {
  name: string;
  projectId?: string;
  status?: string;
  startDate?: Date;
  endDate?: Date;
}

/**
 * Generic position utilities for any entity with order field
 */

/**
 * Get the position value for any ordered entity
 */
export function getEntityPosition<T extends OrderedEntity>(entity: T): string {
  const positionValue = entity.order;
  
  // Handle string positions (fractional indexing)
  if (typeof positionValue === 'string') {
    return validateAndNormalizePosition(positionValue, entity.id);
  }
  
  // Fallback to default position
  return convertIntegerToPosition(1);
}

/**
 * Sort entities by their position (fractional ordering)
 */
export function sortEntitiesByPosition<T extends OrderedEntity>(entities: T[]): T[] {
  return [...entities].sort((a, b) => {
    const posA = getEntityPosition(a);
    const posB = getEntityPosition(b);

    const positionComparison = comparePositions(posA, posB);
    if (positionComparison !== 0) {
      return positionComparison;
    }

    // Tie-breaker using creation date for stability
    const aCreated = new Date(a.createdAt).getTime();
    const bCreated = new Date(b.createdAt).getTime();
    return aCreated - bCreated;
  });
}

/**
 * Generate position for a new entity (at the end of the list)
 */
export function generateNewEntityPosition<T extends OrderedEntity>(entities: T[]): string {
  const sortedEntities = sortEntitiesByPosition(entities);
  const lastEntity = sortedEntities.length > 0 ? sortedEntities[sortedEntities.length - 1] : null;
  const lastPosition = lastEntity ? getEntityPosition(lastEntity) : null;
  return generateInsertPosition(lastPosition, null);
}

/**
 * Generate position for inserting an entity between two others
 */
export function generateEntityInsertPosition<T extends OrderedEntity>(
  beforeEntity: T | null,
  afterEntity: T | null
): string {
  const beforePosition = beforeEntity ? getEntityPosition(beforeEntity) : null;
  const afterPosition = afterEntity ? getEntityPosition(afterEntity) : null;
  return generateInsertPosition(beforePosition, afterPosition);
}

/**
 * Analyze and detect duplicate positions in an entity list
 */
export function analyzeEntityPositions<T extends OrderedEntity>(
  entities: T[],
  label: string = 'Entities'
): {
  totalEntities: number;
  uniquePositions: number;
  duplicatePositions: string[];
  duplicateCount: number;
  analysis: string;
} {
  const positions = entities.map(entity => getEntityPosition(entity));
  const positionCounts = new Map<string, number>();
  
  // Count occurrences of each position
  positions.forEach(pos => {
    positionCounts.set(pos, (positionCounts.get(pos) || 0) + 1);
  });
  
  // Find duplicates
  const duplicatePositions = Array.from(positionCounts.entries())
    .filter(([_, count]) => count > 1)
    .map(([pos, _]) => pos);
  
  const duplicateCount = duplicatePositions.reduce((sum, pos) => sum + positionCounts.get(pos)! - 1, 0);
  
  let analysis = `${label}: ${entities.length} entities, ${positionCounts.size} unique positions`;
  if (duplicateCount > 0) {
    analysis += `, ${duplicateCount} duplicate positions detected`;
  }
  
  debugLog(`[FractionalOrdering] ${analysis}`);
  
  if (duplicatePositions.length > 0) {
    debugWarn(`[FractionalOrdering] Duplicate positions in ${label}:`, 
      duplicatePositions.map(pos => ({
        position: pos,
        count: positionCounts.get(pos),
        entities: entities.filter(e => getEntityPosition(e) === pos).map(e => ({
          id: e.id.slice(-6),
          name: 'name' in e ? (e as any).name.slice(0, 20) : 'Unknown'
        }))
      }))
    );
  }
  
  return {
    totalEntities: entities.length,
    uniquePositions: positionCounts.size,
    duplicatePositions,
    duplicateCount,
    analysis
  };
}

/**
 * TASK GROUP SPECIFIC UTILITIES
 */

/**
 * Sort task groups by their position
 */
export function sortTaskGroupsByPosition(taskGroups: TaskGroupForOrdering[]): TaskGroupForOrdering[] {
  return sortEntitiesByPosition(taskGroups);
}

/**
 * Generate position for a new task group (at the end of the list)
 */
export function generateNewTaskGroupPosition(taskGroups: TaskGroupForOrdering[]): string {
  return generateNewEntityPosition(taskGroups);
}

/**
 * Generate position for inserting a task group between two others
 */
export function generateTaskGroupInsertPosition(
  beforeGroup: TaskGroupForOrdering | null,
  afterGroup: TaskGroupForOrdering | null
): string {
  return generateEntityInsertPosition(beforeGroup, afterGroup);
}

/**
 * Analyze task group positions for duplicates
 */
export function analyzeTaskGroupPositions(
  taskGroups: TaskGroupForOrdering[],
  context: string = 'Task Groups'
): ReturnType<typeof analyzeEntityPositions> {
  return analyzeEntityPositions(taskGroups, context);
}

/**
 * Debug helper for task group positions
 */
export function debugTaskGroupPositions(
  taskGroups: TaskGroupForOrdering[],
  label: string = 'Task Groups'
): void {
  const analysis = analyzeTaskGroupPositions(taskGroups, label);
  
  debugLog(`[DEBUG] ${label} positions:`, 
    taskGroups.map(group => ({
      id: group.id.slice(-6),
      name: group.name.slice(0, 20),
      position: getEntityPosition(group),
      order: group.order
    }))
  );
  
  if (analysis.duplicateCount > 0) {
    debugError(`[DEBUG] ${label} has ${analysis.duplicateCount} duplicate positions that need fixing!`);
  }
}

/**
 * SPRINT SPECIFIC UTILITIES
 */

/**
 * Sort sprints by their position
 */
export function sortSprintsByPosition(sprints: SprintForOrdering[]): SprintForOrdering[] {
  return sortEntitiesByPosition(sprints);
}

/**
 * Generate position for a new sprint (at the end of the list)
 */
export function generateNewSprintPosition(sprints: SprintForOrdering[]): string {
  return generateNewEntityPosition(sprints);
}

/**
 * Generate position for inserting a sprint between two others
 */
export function generateSprintInsertPosition(
  beforeSprint: SprintForOrdering | null,
  afterSprint: SprintForOrdering | null
): string {
  return generateEntityInsertPosition(beforeSprint, afterSprint);
}

/**
 * Analyze sprint positions for duplicates
 */
export function analyzeSprintPositions(
  sprints: SprintForOrdering[],
  context: string = 'Sprints'
): ReturnType<typeof analyzeEntityPositions> {
  return analyzeEntityPositions(sprints, context);
}

/**
 * Debug helper for sprint positions
 */
export function debugSprintPositions(
  sprints: SprintForOrdering[],
  label: string = 'Sprints'
): void {
  const analysis = analyzeSprintPositions(sprints, label);
  
  debugLog(`[DEBUG] ${label} positions:`, 
    sprints.map(sprint => ({
      id: sprint.id.slice(-6),
      name: sprint.name.slice(0, 20),
      position: getEntityPosition(sprint),
      order: sprint.order,
      status: 'status' in sprint ? sprint.status : 'N/A'
    }))
  );
  
  if (analysis.duplicateCount > 0) {
    debugError(`[DEBUG] ${label} has ${analysis.duplicateCount} duplicate positions that need fixing!`);
  }
}

/**
 * RECOVERY UTILITIES FOR ENTITIES
 */

/**
 * Recover from duplicate positions for any entity type
 */
export function recoverEntityDuplicatePositions<T extends OrderedEntity>(
  entities: T[],
  label: string = 'Entities'
): Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }> {
  const analysis = analyzeEntityPositions(entities, label);
  
  if (analysis.duplicateCount === 0) {
    debugLog(`[FractionalOrdering] No duplicate positions to recover for ${label}`);
    return [];
  }
  
  debugLog(`[FractionalOrdering] Starting recovery for ${analysis.duplicateCount} duplicate positions in ${label}`);
  
  const recoveryResults: Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }> = [];
  const positionCounts = new Map<string, T[]>();
  
  // Group entities by position
  entities.forEach(entity => {
    const position = getEntityPosition(entity);
    if (!positionCounts.has(position)) {
      positionCounts.set(position, []);
    }
    positionCounts.get(position)!.push(entity);
  });
  
  // Find and fix duplicates
  positionCounts.forEach((entitiesWithSamePosition, position) => {
    if (entitiesWithSamePosition.length > 1) {
      debugLog(`[FractionalOrdering] Recovering ${entitiesWithSamePosition.length} ${label.toLowerCase()} with duplicate position: ${position}`);
      
      // Keep the first entity (by creation date) in the original position
      const sortedEntities = entitiesWithSamePosition.sort((a, b) => {
        const aCreated = new Date(a.createdAt).getTime();
        const bCreated = new Date(b.createdAt).getTime();
        return aCreated - bCreated;
      });
      
      // Generate new unique positions for the rest
      for (let i = 1; i < sortedEntities.length; i++) {
        const entity = sortedEntities[i];
        const timestamp = Date.now().toString(36);
        const randomSuffix = Math.random().toString(36).substring(2, 5);
        const newPosition = `${position}_${timestamp}_${randomSuffix}`;
        
        recoveryResults.push({
          entityId: entity.id,
          oldOrder: position,
          newOrder: newPosition,
          reason: `Duplicate position recovery - kept oldest ${label.toLowerCase().slice(0, -1)} (${sortedEntities[0].id.slice(-6)}) in original position`
        });
      }
    }
  });
  
  debugLog(`[FractionalOrdering] Recovery complete for ${label}: ${recoveryResults.length} positions reassigned`);
  
  return recoveryResults;
}

/**
 * Validate and recover entity positions
 */
export function validateAndRecoverEntityPositions<T extends OrderedEntity>(
  entities: T[],
  label: string = 'Entities',
  options: {
    autoRecover?: boolean;
    logLevel?: 'debug' | 'warn' | 'error';
  } = {}
): {
  isValid: boolean;
  analysis: ReturnType<typeof analyzeEntityPositions>;
  recoveryActions?: Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }>;
} {
  const { autoRecover = false, logLevel = 'warn' } = options;
  
  const analysis = analyzeEntityPositions(entities, label);
  const isValid = analysis.duplicateCount === 0;
  
  if (!isValid) {
    const message = `${label} position validation failed: ${analysis.duplicateCount} duplicate positions detected`;
    
    if (logLevel === 'error') {
      debugError(`[FractionalOrdering] ${message}`);
    } else {
      debugWarn(`[FractionalOrdering] ${message}`);
    }
    
    if (autoRecover) {
      const recoveryActions = recoverEntityDuplicatePositions(entities, label);
      return {
        isValid: false,
        analysis,
        recoveryActions
      };
    }
  }
  
  return {
    isValid,
    analysis
  };
}

/**
 * TASK GROUP RECOVERY UTILITIES
 */

/**
 * Recover task group duplicate positions
 */
export function recoverTaskGroupDuplicatePositions(
  taskGroups: TaskGroupForOrdering[]
): Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }> {
  return recoverEntityDuplicatePositions(taskGroups, 'Task Groups');
}

/**
 * Validate and recover task group positions
 */
export function validateAndRecoverTaskGroupPositions(
  taskGroups: TaskGroupForOrdering[],
  options: {
    autoRecover?: boolean;
    logLevel?: 'debug' | 'warn' | 'error';
  } = {}
): {
  isValid: boolean;
  analysis: ReturnType<typeof analyzeEntityPositions>;
  recoveryActions?: Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }>;
} {
  return validateAndRecoverEntityPositions(taskGroups, 'Task Groups', options);
}

/**
 * SPRINT RECOVERY UTILITIES
 */

/**
 * Recover sprint duplicate positions
 */
export function recoverSprintDuplicatePositions(
  sprints: SprintForOrdering[]
): Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }> {
  return recoverEntityDuplicatePositions(sprints, 'Sprints');
}

/**
 * Validate and recover sprint positions
 */
export function validateAndRecoverSprintPositions(
  sprints: SprintForOrdering[],
  options: {
    autoRecover?: boolean;
    logLevel?: 'debug' | 'warn' | 'error';
  } = {}
): {
  isValid: boolean;
  analysis: ReturnType<typeof analyzeEntityPositions>;
  recoveryActions?: Array<{ entityId: string; oldOrder: string; newOrder: string; reason: string }>;
} {
  return validateAndRecoverEntityPositions(sprints, 'Sprints', options);
}

/**
 * CONVENIENCE FUNCTIONS FOR DRAG AND DROP
 */

/**
 * Generate reorder position for task groups during drag and drop
 */
export function generateTaskGroupReorderPosition(
  draggedGroup: TaskGroupForOrdering,
  targetGroup: TaskGroupForOrdering,
  allGroups: TaskGroupForOrdering[],
  dropPosition: 'above' | 'below'
): string {
  const sortedGroups = sortTaskGroupsByPosition(allGroups);
  const targetIndex = sortedGroups.findIndex(g => g.id === targetGroup.id);
  
  if (targetIndex === -1) {
    debugError('[TaskGroup Reorder] Target group not found in sorted list');
    return generateNewTaskGroupPosition(allGroups);
  }
  
  let beforeGroup: TaskGroupForOrdering | null = null;
  let afterGroup: TaskGroupForOrdering | null = null;
  
  if (dropPosition === 'above') {
    beforeGroup = targetIndex > 0 ? sortedGroups[targetIndex - 1] : null;
    afterGroup = sortedGroups[targetIndex];
  } else {
    beforeGroup = sortedGroups[targetIndex];
    afterGroup = targetIndex < sortedGroups.length - 1 ? sortedGroups[targetIndex + 1] : null;
  }
  
  return generateTaskGroupInsertPosition(beforeGroup, afterGroup);
}

/**
 * Generate reorder position for sprints during drag and drop
 */
export function generateSprintReorderPosition(
  draggedSprint: SprintForOrdering,
  targetSprint: SprintForOrdering,
  allSprints: SprintForOrdering[],
  dropPosition: 'above' | 'below'
): string {
  const sortedSprints = sortSprintsByPosition(allSprints);
  const targetIndex = sortedSprints.findIndex(s => s.id === targetSprint.id);
  
  if (targetIndex === -1) {
    debugError('[Sprint Reorder] Target sprint not found in sorted list');
    return generateNewSprintPosition(allSprints);
  }
  
  let beforeSprint: SprintForOrdering | null = null;
  let afterSprint: SprintForOrdering | null = null;
  
  if (dropPosition === 'above') {
    beforeSprint = targetIndex > 0 ? sortedSprints[targetIndex - 1] : null;
    afterSprint = sortedSprints[targetIndex];
  } else {
    beforeSprint = sortedSprints[targetIndex];
    afterSprint = targetIndex < sortedSprints.length - 1 ? sortedSprints[targetIndex + 1] : null;
  }
  
  return generateSprintInsertPosition(beforeSprint, afterSprint);
} 
